import * as z from 'zod';
import { get, keys, isString, map } from 'lodash-es';
import debug from 'debug';
import { AxiosError, AxiosResponse } from 'axios';
import { hooks } from './hooks';
import { ErrorModalOptions } from './modal';
import * as YAML from 'yaml';

const log = debug('itp:core:errors');

function isAxiosError(payload: any): payload is AxiosError {
  return payload['isAxiosError'] === true;
}

function isNetworkError(payload: any) {
  return get(payload, 'message') === 'Network Error';
}

function isJSONResponse(resp: AxiosResponse) {
  return (resp.headers['content-type'] as string).includes('application/json');
}

function isAbv(value: any): value is ArrayBuffer {
  return (
    value && value instanceof ArrayBuffer && value.byteLength !== undefined
  );
}

function responseJSON(response: AxiosResponse) {
  if (isAbv(response.data)) {
    try {
      return JSON.parse(new TextDecoder().decode(response.data));
    } catch (error) {
      console.error('failed to parse response data as JSON', error);
    }
  }
  return response.data;
}

function humanizeAxiosError(e: AxiosError): ErrorModalOptions {
  if (isNetworkError(e)) {
    return { error: e.message };
  }

  if (!e.response || !isJSONResponse(e.response)) {
    return { error: e };
  }

  const title = `${e.response.status} ${e.response.statusText}`,
    message = e.message,
    data = responseJSON(e.response);

  const errorMessage = get(data, 'message', null),
    errorCode = get(data, 'code', null);

  if (errorMessage && errorCode) {
    return {
      title: errorCode,
      message: errorMessage,
    };
  }

  return {
    title,
    message: message,
    details: humanizeDetails(data),
  };
}

function humanizeDetails(data: any) {
  if (isString(data)) {
    return data;
  }

  if (isValidationErrorContent(data)) {
    if (data.detail.length === 1) {
      return humanValidationError(data.detail[0]);
    }
    return YAML.stringify(map(data.detail, humanValidationError));
  }

  const keys_ = keys(data);

  if (keys_.length === 1) {
    data = data[keys_[0]];
  }

  return YAML.stringify(data);
}

const ValidationErrorSchema = z.object({
  type: z.literal('ValidationError'),
  loc: z.array(z.string()),
  msg: z.string(),
});

const ValidationErrorContentSchema = z.object({
  detail: z.array(ValidationErrorSchema),
});

function isValidationErrorContent(value: any): value is ValidationErrorContent {
  const p = ValidationErrorContentSchema.safeParse(value);
  log('isValidationErrorContent? %o => %o', value, p);
  return p.success;
}

function humanValidationError(err: ValidationError) {
  return `${err.loc.join('.')}: ${err.msg}`;
}

interface ValidationError {
  loc: string[];
  msg: string;
}

interface ValidationErrorContent {
  detail: ValidationError[];
}

export function initializeErrorModalOptionsHookHandler() {
  hooks.errorModalOptionsHook.add((opts) => {
    if (opts.error && isAxiosError(opts.error)) {
      return humanizeAxiosError(opts.error);
    }
    return opts;
  });
}
